"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.postContraintCheck = exports.getBlogConstraints = exports.getSocialConstraints = exports.postRequiresSubject = exports.fileCount = void 0;
const v4_1 = require("zod/v4");
const tiktok_media_1 = require("./tiktok-media");
const platform_enum_1 = require("./types/platform.enum");
const post_status_enum_1 = require("./types/post_status.enum");
exports.fileCount = v4_1.z.number();
/**
 * Whether the post's `subject` field is used as a title for at least one of
 * the selected platforms. Blog platforms use it as the post title; YouTube
 * (including Shorts, which is posted under `Platform.YOUTUBE`) uses it as
 * the video title. Purely social platforms don't surface it anywhere, so a
 * social-only post legitimately stores `subject: ""`.
 */
const postRequiresSubject = (platforms) => platforms.some((p) => (0, platform_enum_1.isBlogPlatform)(p) || p === platform_enum_1.Platform.YOUTUBE);
exports.postRequiresSubject = postRequiresSubject;
const getSocialConstraints = (platform) => {
    const platformData = platform_enum_1.Platforms[platform];
    if (platformData.type === platform_enum_1.PlatformType.SOCIAL) {
        return platformData.constraints;
    }
    throw new Error(`Platform ${platform} is not a social platform`);
};
exports.getSocialConstraints = getSocialConstraints;
const getBlogConstraints = (platform) => {
    const platformData = platform_enum_1.Platforms[platform];
    if (platformData.type === platform_enum_1.PlatformType.BLOG) {
        return platformData.constraints;
    }
    throw new Error(`Platform ${platform} is not a blog platform`);
};
exports.getBlogConstraints = getBlogConstraints;
const contentMaxLengthMessage = (maxLength) => `Post copy content must be no longer than ${maxLength} characters.`;
const contentLengthMap = {
    [platform_enum_1.Platform.FACEBOOK]: v4_1.z
        .string()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.FACEBOOK).message.min, `Post copy content must be at least ${(0, exports.getSocialConstraints)(platform_enum_1.Platform.FACEBOOK).message.min} characters long.`)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.FACEBOOK).message.max, contentMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.FACEBOOK).message.max)),
    [platform_enum_1.Platform.INSTAGRAM]: v4_1.z
        .string()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.INSTAGRAM).message.min, `Post copy content must be at least ${(0, exports.getSocialConstraints)(platform_enum_1.Platform.INSTAGRAM).message.min} characters long.`)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.INSTAGRAM).message.max, contentMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.INSTAGRAM).message.max)),
    [platform_enum_1.Platform.THREADS]: v4_1.z
        .string()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.THREADS).message.min, `Post copy content must be at least ${(0, exports.getSocialConstraints)(platform_enum_1.Platform.THREADS).message.min} characters long.`)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.THREADS).message.max, contentMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.THREADS).message.max)),
    [platform_enum_1.Platform.LINKEDIN]: v4_1.z
        .string()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.LINKEDIN).message.min, `Post copy content must be at least ${(0, exports.getSocialConstraints)(platform_enum_1.Platform.LINKEDIN).message.min} characters long.`)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.LINKEDIN).message.max, contentMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.LINKEDIN).message.max)),
    [platform_enum_1.Platform.WORDPRESS]: v4_1.z
        .string()
        .min((0, exports.getBlogConstraints)(platform_enum_1.Platform.WORDPRESS).content.min, `Post copy content must be at least ${(0, exports.getBlogConstraints)(platform_enum_1.Platform.WORDPRESS).content.min} characters long.`)
        .max((0, exports.getBlogConstraints)(platform_enum_1.Platform.WORDPRESS).content.max, contentMaxLengthMessage((0, exports.getBlogConstraints)(platform_enum_1.Platform.WORDPRESS).content.max)),
    [platform_enum_1.Platform.CUSTOM_BLOG]: v4_1.z
        .string()
        .min((0, exports.getBlogConstraints)(platform_enum_1.Platform.CUSTOM_BLOG).content.min, `Post copy content must be at least ${(0, exports.getBlogConstraints)(platform_enum_1.Platform.CUSTOM_BLOG).content.min} characters long.`)
        .max((0, exports.getBlogConstraints)(platform_enum_1.Platform.CUSTOM_BLOG).content.max, contentMaxLengthMessage((0, exports.getBlogConstraints)(platform_enum_1.Platform.CUSTOM_BLOG).content.max)),
    [platform_enum_1.Platform.YOUTUBE]: v4_1.z
        .string()
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.YOUTUBE).message.max, contentMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.YOUTUBE).message.max)),
    [platform_enum_1.Platform.TIKTOK]: v4_1.z
        .string()
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.TIKTOK).message.max, contentMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.TIKTOK).message.max)),
    [platform_enum_1.Platform.X]: v4_1.z
        .string()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.X).message.min, `Post copy content must be at least ${(0, exports.getSocialConstraints)(platform_enum_1.Platform.X).message.min} characters long.`)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.X).message.max, contentMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.X).message.max)),
    [platform_enum_1.Platform.GOOGLE_BUSINESS]: v4_1.z
        .string()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.GOOGLE_BUSINESS).message.min, `Post copy content must be at least ${(0, exports.getSocialConstraints)(platform_enum_1.Platform.GOOGLE_BUSINESS).message.min} characters long.`)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.GOOGLE_BUSINESS).message.max, contentMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.GOOGLE_BUSINESS).message.max)),
};
const filesMinLengthMessage = (minLength) => `Minimum number of images/videos per post is ${minLength}. Please attach at least ${minLength} ${minLength === 1 ? "image/video" : "images/videos"}.`;
const filesMaxLengthMessage = (maxLength) => `Exceeded maximum number of images/videos per post. Maximum is ${maxLength}.`;
const filesCountMap = {
    [platform_enum_1.Platform.FACEBOOK]: v4_1.z
        .number()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.FACEBOOK).media.min)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.FACEBOOK).media.max, filesMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.FACEBOOK).media.max)),
    [platform_enum_1.Platform.INSTAGRAM]: v4_1.z
        .number()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.INSTAGRAM).media.min, filesMinLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.INSTAGRAM).media.min))
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.INSTAGRAM).media.max, filesMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.INSTAGRAM).media.max)),
    [platform_enum_1.Platform.THREADS]: v4_1.z
        .number()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.THREADS).media.min)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.THREADS).media.max, filesMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.THREADS).media.max)),
    [platform_enum_1.Platform.LINKEDIN]: v4_1.z
        .number()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.LINKEDIN).media.min)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.LINKEDIN).media.max, filesMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.LINKEDIN).media.max)),
    [platform_enum_1.Platform.WORDPRESS]: v4_1.z
        .number()
        .min((0, exports.getBlogConstraints)(platform_enum_1.Platform.WORDPRESS).media.min)
        .max((0, exports.getBlogConstraints)(platform_enum_1.Platform.WORDPRESS).media.max, filesMaxLengthMessage((0, exports.getBlogConstraints)(platform_enum_1.Platform.WORDPRESS).media.max)),
    [platform_enum_1.Platform.CUSTOM_BLOG]: v4_1.z
        .number()
        .min((0, exports.getBlogConstraints)(platform_enum_1.Platform.CUSTOM_BLOG).media.min)
        .max((0, exports.getBlogConstraints)(platform_enum_1.Platform.CUSTOM_BLOG).media.max, filesMaxLengthMessage((0, exports.getBlogConstraints)(platform_enum_1.Platform.CUSTOM_BLOG).media.max)),
    [platform_enum_1.Platform.YOUTUBE]: v4_1.z
        .number()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.YOUTUBE).media.min, filesMinLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.YOUTUBE).media.min))
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.YOUTUBE).media.max, filesMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.YOUTUBE).media.max)),
    [platform_enum_1.Platform.TIKTOK]: v4_1.z
        .number()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.TIKTOK).media.min, filesMinLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.TIKTOK).media.min))
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.TIKTOK).media.max, filesMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.TIKTOK).media.max)),
    [platform_enum_1.Platform.X]: v4_1.z
        .number()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.X).media.min)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.X).media.max, filesMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.X).media.max)),
    [platform_enum_1.Platform.GOOGLE_BUSINESS]: v4_1.z
        .number()
        .min((0, exports.getSocialConstraints)(platform_enum_1.Platform.GOOGLE_BUSINESS).media.min)
        .max((0, exports.getSocialConstraints)(platform_enum_1.Platform.GOOGLE_BUSINESS).media.max, filesMaxLengthMessage((0, exports.getSocialConstraints)(platform_enum_1.Platform.GOOGLE_BUSINESS).media.max)),
};
const postContraintCheck = (platform, content, files = [], mediaFiles, context) => {
    // Type guard to ensure we only handle social platforms
    if (!((0, platform_enum_1.isSocialPlatform)(platform) || (0, platform_enum_1.isBlogPlatform)(platform))) {
        throw new Error(`Platform ${platform} is not supported for post constraints`);
    }
    const skipMediaMin = ((context === null || context === void 0 ? void 0 : context.status) === post_status_enum_1.PostStatus.PUBLISHED ||
        (platform === platform_enum_1.Platform.YOUTUBE && (context === null || context === void 0 ? void 0 : context.refId))) &&
        files.length === 0;
    const fileCount = skipMediaMin ? 1 : files.length;
    const filesSchema = filesCountMap[platform];
    const contentSchema = contentLengthMap[platform];
    if (!filesSchema || !contentSchema) {
        return {
            valid: true,
            errors: [],
            errorMessage: "",
            warnings: [],
        };
    }
    const filesCountCheck = filesSchema.safeParse(fileCount);
    const contentLengthCheck = contentSchema.safeParse(content);
    let valid = filesCountCheck.success && contentLengthCheck.success;
    const errors = [filesCountCheck.error, contentLengthCheck.error];
    const extraMessages = [];
    const warnings = [];
    if (platform === platform_enum_1.Platform.TIKTOK && mediaFiles && mediaFiles.length > 0) {
        const tiktokCheck = (0, tiktok_media_1.validateTikTokMedia)(mediaFiles);
        if (!tiktokCheck.valid) {
            valid = false;
            extraMessages.push(...tiktokCheck.errors);
        }
        warnings.push(...tiktokCheck.warnings);
    }
    const errorMessage = [
        errors
            .reduce((acc, curr) => {
            var _a;
            if ((_a = curr === null || curr === void 0 ? void 0 : curr.issues) === null || _a === void 0 ? void 0 : _a.length) {
                const message = acc.concat(" ", curr.issues
                    .map((issue) => issue.message)
                    .join(" "));
                return message;
            }
            return acc;
        }, "")
            .trim(),
        ...extraMessages,
    ]
        .filter(Boolean)
        .join(" ")
        .trim();
    return {
        valid,
        errors,
        errorMessage,
        warnings,
    };
};
exports.postContraintCheck = postContraintCheck;
